/**
 * Right-click quick actions, with the two things that make them usable by
 * everyone.
 *
 * **Right-click is never the only path.** Touch has no right-click at all, and a
 * keyboard user has no way to fire one. Every entity row therefore also carries a
 * three-dot `⋯` button (36px square at least, `aria-haspopup="menu"`) that opens
 * the *same* menu — see `MoreButton` below. The handoff states this as a
 * requirement, not a nicety.
 *
 * **The card an iPad shows in a row's place carries it too, built by the same
 * function** (Eden, 9 October 2026: the menu options and the dots' position stay
 * consistent across desktop and iPad). An entity's lines come from ONE builder
 * (`lib/row-actions.ts`), so a row and its card cannot offer different menus.
 *
 * **ARIA menu semantics and a keyboard path.** `role="menu"` with
 * `role="menuitem"` children, arrow keys to move, Home/End to jump, Enter to
 * activate, Escape to close and restore focus. A div that merely looks like a
 * menu announces nothing.
 *
 * Position is fixed at the cursor and **clamped to the viewport** — a menu opened
 * on the last row of a full-height table otherwise renders partly off-screen with
 * its bottom items unreachable.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";

export interface MenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /**
   * Rendered as a trailing note, e.g. "concept" for an unbacked action, or the
   * REASON a disabled item is disabled — "no GPS fix". On a disabled item it is
   * wired to `aria-describedby`, so a screen reader announces the reason with the
   * item rather than leaving it as decoration beside it.
   */
  hint?: string;
}

export interface MenuTarget {
  x: number;
  y: number;
  title: string;
  items: MenuItem[];
}

const MENU_WIDTH = 220;

export function ContextMenu({ target, onClose }: { target: MenuTarget; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const hintIdBase = useId();
  const [position, setPosition] = useState({ left: target.x, top: target.y });
  const [activeIndex, setActiveIndex] = useState(0);

  /*
   * Arrow keys walk EVERY item, including the disabled ones.
   *
   * That is the fix for TRBUD-215, and it is the half that a change of attribute
   * alone would not have delivered: a disabled item nobody can reach still never
   * announces its reason. `aria-disabled` items stay in the menu's navigation
   * order — the ARIA Authoring Practices are explicit about it — precisely so the
   * user can find out WHY an action is unavailable instead of finding it missing.
   *
   * It also repairs a counting bug that had been here the whole time. The index
   * was bounded by the ENABLED count while `focus()` indexed into the full
   * `[role='menuitem']` NodeList, so on the Assets row menu — five items, one of
   * them disabled — ArrowDown cycled 0..3 and "Full repair history", the last
   * item, was unreachable by keyboard. Two lists, one index.
   */
  const itemCount = Math.max(1, target.items.length);

  useLayoutEffect(() => {
    // Measured after mount rather than estimated: the height depends on how many
    // items this entity has, and guessing it is how a menu ends up clipped.
    const element = ref.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const margin = 8;
    const left = Math.min(target.x, window.innerWidth - Math.max(rect.width, MENU_WIDTH) - margin);
    const top = Math.min(target.y, window.innerHeight - rect.height - margin);
    setPosition({ left: Math.max(margin, left), top: Math.max(margin, top) });
  }, [target.x, target.y, target.items.length]);

  useEffect(() => {
    const items = ref.current?.querySelectorAll<HTMLButtonElement>("[role='menuitem']");
    items?.[activeIndex]?.focus();
  }, [activeIndex]);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) onClose();
    }
    function onKeyDown(event: KeyboardEvent) {
      switch (event.key) {
        case "Escape":
          event.preventDefault();
          onClose();
          break;
        case "ArrowDown":
          event.preventDefault();
          setActiveIndex((i) => (i + 1) % itemCount);
          break;
        case "ArrowUp":
          event.preventDefault();
          setActiveIndex((i) => (i - 1 + itemCount) % itemCount);
          break;
        case "Home":
          event.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          event.preventDefault();
          setActiveIndex(target.items.length - 1 < 0 ? 0 : target.items.length - 1);
          break;
        default:
          break;
      }
    }
    // Capture so the menu wins over a row's own click handler.
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    // Any scroll invalidates a cursor-anchored position, so close rather than
    // follow — a menu that drifts away from what it acts on is worse than none.
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose, itemCount, target.items.length]);

  return (
    <div
      ref={ref}
      className="ctx-menu"
      role="menu"
      aria-label={target.title}
      style={{ left: position.left, top: position.top, minWidth: MENU_WIDTH }}
    >
      <div className="ctx-header">{target.title}</div>
      {target.items.map((item) => {
        /*
         * ── `aria-disabled`, never the `disabled` attribute — TRBUD-215 ──
         *
         * A native `<button disabled>` is removed from the tab order AND cannot
         * be focused programmatically, so `focus()` above silently did nothing
         * on it. The consequence found in UAT 0909: the Assets row menu's "Show
         * on map" line carries the reason a row cannot be mapped — "no GPS fix"
         * — and a keyboard or screen-reader user could never land on it, so the
         * action looked simply absent and the explanation was unreachable. The
         * hint was visible only to somebody who could already see the greyed-out
         * line.
         *
         * `aria-disabled` announces the state, keeps the element focusable, and
         * leaves the activation to be refused in code — which is why the two
         * handlers below early-return rather than relying on the attribute to
         * stop them. That refusal is the load-bearing half: with `disabled` gone,
         * nothing else prevents a click.
         */
        const disabled = item.disabled === true;
        const hintId = item.hint === undefined ? undefined : `${hintIdBase}-${item.key}`;
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className="ctx-item"
            aria-disabled={disabled ? "true" : undefined}
            /*
             * The hint is tied to the item only when the item is DISABLED, where
             * it is the reason and has to be announced. On an enabled item it is
             * an aside ("opens the queue", "on the record") and describing every
             * item with it would make the menu read twice as long for no gain.
             */
            aria-describedby={disabled ? hintId : undefined}
            tabIndex={-1}
            onClick={() => {
              if (disabled) return;
              onClose();
              item.onSelect();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (disabled) return;
                onClose();
                item.onSelect();
              }
            }}
          >
            {item.label}
            {item.hint ? (
              <span
                id={hintId}
                className="text-muted"
                style={{ fontSize: "var(--tb-text-2xs)", marginLeft: 6 }}
              >
                {item.hint}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The keyboard- and touch-reachable twin of right-click: a three-dot button.
 *
 * **Reads as the three-dot icon "⋯" everywhere (Eden, 8 October 2026: "I asked
 * for the Actions dropdown menu to become a triple dot icon across the site. I
 * am still seeing the Action drop down button").** That reverses the ruling of
 * 14 August 2026, when the label became **"Actions ▾"** because "⋯ more" was
 * unclear. The reason for the reversal is the same one that made the Assets
 * register and the kanban cards opt in to the icon early: a column of identical
 * word buttons competes with the data beside it on a row that is already
 * clickable and right-clickable, and on the work-order queue the word button
 * could not sit beside the stage dropdown without squeezing it. So the icon is
 * the DEFAULT now and `icon={false}` is not used by any screen;
 * `ContextMenu.test.tsx` fails if one starts to.
 *
 * What the 14 August ruling was RIGHT about still holds, and is why the accessible
 * name is mandatory in the type: an icon has no text, so the button's name has to
 * say what it opens and for which row. Every caller passes `label` or `entity`
 * ("Actions for WO-1193"), and `aria-haspopup="menu"` tells a screen reader this
 * opens a menu rather than performing an action.
 *
 * The target is square, 36px at least, and 56px (`--touch-target-min`) on a
 * coarse pointer — `.more-button` in `industry.css` carries the floor rather than
 * an inline style, because an inline `min-width` would beat a stylesheet rule that
 * is not `!important`.
 */
export function MoreButton({
  onOpen,
  label,
  entity,
  icon = true,
}: {
  onOpen: (point: { x: number; y: number }) => void;
  /**
   * Render the single glyph. **The default, and the only form any screen uses**
   * (see above); `false` restores the old word "Actions ▾" and exists only so a
   * future ruling can bring it back for one place without rebuilding the control.
   * The ACCESSIBLE name is the same either way.
   */
  icon?: boolean;
} & (
  | {
      /** Full accessible name, e.g. "Actions for WO-1193". */
      label: string;
      entity?: string;
    }
  | {
      /** Full accessible name. Defaults from `entity`. */
      label?: string;
      /** What the actions apply to, e.g. "WO-1193" — makes the aria-label specific. */
      entity: string;
    }
)) {
  return (
    <button
      type="button"
      className={icon ? "btn btn-secondary more-button" : "btn btn-secondary"}
      // Fifty rows of three dots is fifty identically-named buttons to a screen
      // reader, so the entity goes in the accessible name — and since the glyph
      // is the only thing on screen, the name is the whole of the label.
      aria-label={label ?? `Actions for ${entity}`}
      aria-haspopup="menu"
      style={{
        fontSize: "var(--tb-text-sm)",
        minHeight: 36,
        // Square in the icon form, so losing the word does not lose the target;
        // the width floor itself lives in `.more-button`, where the coarse
        // pointer's larger floor can override it.
        padding: icon ? "2px 6px" : "2px 10px",
        whiteSpace: "nowrap",
      }}
      onClick={(event) => {
        event.stopPropagation();
        // Anchored to the button, not the pointer — a keyboard activation has no
        // pointer coordinates and would otherwise open the menu at (0, 0).
        const rect = event.currentTarget.getBoundingClientRect();
        onOpen({ x: rect.left, y: rect.bottom + 2 });
      }}
    >
      {icon ? (
        // Both glyphs are aria-hidden: the button already has its full name from
        // `aria-label`, and letting "⋯" into the accessible name would append
        // punctuation a screen reader reads aloud.
        <span aria-hidden="true">⋯</span>
      ) : (
        <>
          Actions
          <span aria-hidden="true" style={{ marginLeft: 2 }}>
            ▾
          </span>
        </>
      )}
    </button>
  );
}

/**
 * Hook up right-click on a row.
 *
 * Returned as a prop bag so a row cannot forget `preventDefault` — without it the
 * browser's own context menu opens on top of this one.
 */
export function contextMenuProps(open: (point: { x: number; y: number }) => void) {
  return {
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      open({ x: event.clientX, y: event.clientY });
    },
  };
}

/** Wraps a screen so it can host one menu at a time. */
export function useContextMenu() {
  const [target, setTarget] = useState<MenuTarget | null>(null);
  return {
    target,
    open(point: { x: number; y: number }, title: string, items: MenuItem[]) {
      setTarget({ x: point.x, y: point.y, title, items });
    },
    close: () => setTarget(null),
    render(): ReactNode {
      return target ? <ContextMenu target={target} onClose={() => setTarget(null)} /> : null;
    },
  };
}
